import { coreService } from './core-service'
import { Price, Programme, Workout } from '../dao/core-dao'
import { attributionUtil } from '../util/attribution-util'
import { config } from '../util/config'
import { EventBusMessage } from '../util/event-bus-util'
import { logger } from '../util/logger'
import ReactGA from 'react-ga4'

export enum GoogleAnalyticsEvent {
  LEAD = 'lead',
  COMPLETE_REGISTRATION = 'complete_registration',
  INITIATE_CHECKOUT = 'initiate_checkout',
  PURCHASE = 'purchase',
  WORKOUT_VIDEO_START = 'workout_video_started',
  WORKOUT_VIDEO_FINISH = 'workout_video_finished',
  PROGRAMME_STARTED = 'programme_started',
  PROGRAMME_COMPLETED = 'programme_completed',
  PROGRAMME_CANCEL = 'programme_cancel',
  PROGRAMME_WORKOUT_VIDEO_START = 'programme_workout_video_started',
  PROGRAMME_WORKOUT_VIDEO_FINISH = 'programme_workout_video_finished',
}

export enum GoogleAnalyticsEventCategory {
  REGISTRATION = 'registration',
  CHECKOUT = 'checkout',
  PURCHASE = 'purchase',
  WORKOUT_VIDEO = 'workout_video',
  PROGRAMME_STARTED = 'programme_started',
  PROGRAMME_COMPLETED = 'programme_completed',
  PROGRAMME_CANCEL = 'programme_cancel',
  PROGRAMME_WORKOUT_VIDEO_START = 'programme_workout_video_start',
  PROGRAMME_WORKOUT_VIDEO_FINISH = 'programme_workout_video_finish',
}

export enum GoogleAnalyticsEventAction {
  SUCCESS = 'success',
  INITIATED = 'initiated',
  STARTED = 'started',
  FINISHED = 'finished',
  COMPLETE = 'complete',
  CANCELLED = 'cancelled',
}

export type GoogleAnalyticsEventParams = {
  [key: string]: string | number | boolean | undefined
}

let isInitialized = false

export const googleAnalyticsService = {
  init: (): Promise<void> => {
    return new Promise((resolve) => {
      try {
        if (!config.googleAnalytics) {
          logger.warn('[GA] Google Analytics is not configured. Set REACT_APP_GOOGLE_ANALYTICS_ID environment variable.')
          resolve()
          return
        }

        const { measurementId, debug } = config.googleAnalytics

        if (!measurementId) {
          logger.warn('[GA] Google Analytics measurement ID is not configured')
          resolve()
          return
        }

        // Initialize react-ga4
        ReactGA.initialize(measurementId, {
          testMode: false,
          gtagOptions: {
            debug_mode: debug,
          },
        })

        isInitialized = true
        logger.info('[GA] Google Analytics initialized successfully with react-ga4')
        resolve()
      } catch (error) {
        logger.warn('[GA] Failed to initialize Google Analytics:', error)
        resolve() // Always resolve to not block app initialization
      }
    })
  },

  _push: (params: { eventName: GoogleAnalyticsEvent; eventParams?: GoogleAnalyticsEventParams }): void => {
    if (!isInitialized) {
      // GA not initialized, silently skip
      return
    }
    const { eventName, eventParams } = params
    ReactGA.event(eventName, eventParams)
  },

  sendCheckoutInitiated: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { price }: { price: Price } = message.meta ?? {}

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.INITIATE_CHECKOUT,
      eventParams: {
        product_name: price?.product.name,
        event_category: GoogleAnalyticsEventCategory.CHECKOUT,
        action: GoogleAnalyticsEventAction.INITIATED,
      },
    })
  },

  sendSubscriptionSet: async (_: { message: EventBusMessage }): Promise<void> => {
    const subscriptionItems = await coreService.getMyActiveOrTrialingSubscriptionItems()
    const subscriptionItem = subscriptionItems[0]

    if (subscriptionItem) {
      const { amount, currency } = subscriptionItem.plan
      const { name: productName } = subscriptionItem.product

      googleAnalyticsService._push({
        eventName: GoogleAnalyticsEvent.PURCHASE,
        eventParams: {
          product_name: productName,
          value: amount,
          currency: currency,
          event_category: GoogleAnalyticsEventCategory.PURCHASE,
        },
      })
    }
  },

  sendVideoPlaybackStarted: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { workout }: { workout: Workout } = message.meta
    const trainers = workout.trainers.map((t) => `${t.firstName} ${t.lastName}`).join(', ')
    const workoutTitle = workout.title

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.WORKOUT_VIDEO_START,
      eventParams: {
        workout_title: workoutTitle,
        trainers: trainers,
        event_category: GoogleAnalyticsEventCategory.WORKOUT_VIDEO,
        action: GoogleAnalyticsEventAction.STARTED,
      },
    })
  },

  sendVideoPlaybackEnded: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { workout }: { workout: Workout } = message.meta
    const trainers = workout.trainers.map((t) => `${t.firstName} ${t.lastName}`).join(', ')
    const workoutTitle = workout.title

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.WORKOUT_VIDEO_FINISH,
      eventParams: {
        workout_title: workoutTitle,
        trainers: trainers,
        event_category: GoogleAnalyticsEventCategory.WORKOUT_VIDEO,
        action: GoogleAnalyticsEventAction.FINISHED,
      },
    })
  },

  sendUserCreated: (_: { message: unknown }): void => {
    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.LEAD,
      eventParams: {
        event_category: GoogleAnalyticsEventCategory.REGISTRATION,
        action: GoogleAnalyticsEventAction.INITIATED,
        ...attributionUtil.getAttributionParams(),
      },
    })
  },

  sendRegistrationCompleted: (_: { message: unknown }): void => {
    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.COMPLETE_REGISTRATION,
      eventParams: {
        event_category: GoogleAnalyticsEventCategory.REGISTRATION,
        action: GoogleAnalyticsEventAction.SUCCESS,
        ...attributionUtil.getAttributionParams(),
      },
    })
  },

  sendProgrammeStarted: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { programme }: { programme: Programme } = message.meta

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.PROGRAMME_STARTED,
      eventParams: {
        programme_id: programme.id,
        programme_title: programme.title,
        programme_intensity_id: programme.intensityId,
        event_category: GoogleAnalyticsEventCategory.PROGRAMME_STARTED,
      },
    })
  },

  sendProgrammeCompleted: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { programme }: { programme: Programme } = message.meta

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.PROGRAMME_COMPLETED,
      eventParams: {
        programme_id: programme.id,
        programme_title: programme.title,
        programme_intensity_id: programme.intensityId,
        event_category: GoogleAnalyticsEventCategory.PROGRAMME_COMPLETED,
        action: GoogleAnalyticsEventAction.COMPLETE,
      },
    })
  },

  sendProgrammeCancelled: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { programme }: { programme: Programme } = message.meta

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.PROGRAMME_CANCEL,
      eventParams: {
        programme_id: programme.id,
        programme_title: programme.title,
        programme_intensity_id: programme.intensityId,
        event_category: GoogleAnalyticsEventCategory.PROGRAMME_CANCEL,
        action: GoogleAnalyticsEventAction.CANCELLED,
      },
    })
  },

  sendProgrammeWorkoutVideoStarted: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { workout, programme }: { workout: Workout; programme: Programme } = message.meta
    const trainers = workout.trainers.map((t) => `${t.firstName} ${t.lastName}`).join(', ')
    const workoutTitle = workout.title

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.PROGRAMME_WORKOUT_VIDEO_START,
      eventParams: {
        workout_title: workoutTitle,
        trainers: trainers,
        programme_id: programme.id,
        programme_title: programme.title,
        event_category: GoogleAnalyticsEventCategory.PROGRAMME_WORKOUT_VIDEO_START,
        action: GoogleAnalyticsEventAction.STARTED,
      },
    })
  },

  sendProgrammeWorkoutVideoFinished: (params: { message: EventBusMessage }): void => {
    const { message } = params
    const { workout, programme }: { workout: Workout; programme: Programme } = message.meta
    const trainers = workout.trainers.map((t) => `${t.firstName} ${t.lastName}`).join(', ')
    const workoutTitle = workout.title

    googleAnalyticsService._push({
      eventName: GoogleAnalyticsEvent.PROGRAMME_WORKOUT_VIDEO_FINISH,
      eventParams: {
        workout_title: workoutTitle,
        trainers: trainers,
        programme_id: programme.id,
        programme_title: programme.title,
        event_category: GoogleAnalyticsEventCategory.PROGRAMME_WORKOUT_VIDEO_FINISH,
        action: GoogleAnalyticsEventAction.FINISHED,
      },
    })
  },
}
